/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Col, Form, Row } from 'antd';
import history from 'helpers/history';
import { Input, notification, NOTIFICATION_TYPES, Popup, RichArea, Select, Table, Upload } from '_atoms';
import type { TableColumnType, TableRow } from '_atoms/Table/types';
import type { AppDispatch } from 'store';
import type { CandidateData } from 'store/reducers/candidateReducer';
import {
  ExperienceList,
  FormCreateEdit,
  FormListDoubleInputSelect,
  FormListDoubleSelect,
  FormListSingle,
  FormListSources,
} from '_molecules';
import * as actions from 'actions';
import { getLanguages, getLevels, getLocations, getPositions } from 'store/selectors/commonSelectors';
import {
  getCandidateCompanies,
  getCandidateCountries,
  getCandidateSpecialities,
  getCandidateTags,
} from 'store/selectors/candidateSelectors';
import { getUser } from 'store/selectors/authorizeSelectors';
import { candidateLevels, levels } from 'constants/constants';
import { SELECT_TYPES } from '_atoms/Select/constants';
import {
  EMAIL_VALIDATION,
  PHONE_VALIDATOR,
  SOCIAL_NETWORKS_OPTIONS,
  VALIDATION_MAX_100,
} from '_organisms/CandidateCreationForm/constants';
import { REQUIRED_RULE } from '_organisms/InterviewFeedback/constants';
import { detectNetwork } from '_organisms/CandidateCreationForm/utyls';
import { validatorL } from '_templates/CreateOpening/utils';
import { uploadDocs } from '_templates/CandidateCreation/utils';
import { CHECKBOX_FIELDS, INLINE_EDIT_FIELDS, useFields } from './constants';
import { isFieldSelected, useColumns } from './utils';
import type { CandidateMergeData, CvItem, PopupProps, SourceItem } from './types';
import './style.scss';

const INITIAL_VALUES: CandidateMergeData = {
  offices: [],
  sources: [],
  foreign_languages: [],
  phone: [],
  email: [],
  telegram: '',
  social_networks: [],
  specialities: [],
  cvs: [],
  cvs_links: [],
  tags: [],
  experience: [],
};

export const MergeProfile = () => {
  const { t } = useTranslation();
  const { id, duplicate_id } = useParams<{ id: string; duplicate_id: string }>();
  const dispatch = useDispatch<AppDispatch>();

  const fields = useFields();

  const user = useSelector(getUser);

  const [currentCandidate, setCurrentCandidate] = useState<CandidateMergeData>(INITIAL_VALUES);
  const [existingCandidate, setExistingCandidate] = useState<CandidateMergeData>(INITIAL_VALUES);
  const [selectedValues, setSelectedValues] = useState<CandidateMergeData>(INITIAL_VALUES);
  const [loadingCandidate, setLoadingCandidate] = useState(true);
  const [loadingSSCCandidate, setLoadingSSCCandidate] = useState(true);
  const [editing, setEditing] = useState<Record<string, boolean>>({});
  const [modalField, setModalField] = useState('');
  const [isEdited, setIsEdited] = useState(false);
  const [discardPopup, setDiscardPopup] = useState(false);
  const [mergeCheckPopup, setMergeCheckPopup] = useState(false);
  const [error, setError] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [activeCheckboxes, setActiveCheckboxes] = useState<Record<string, { current: boolean; existing: boolean }>>({
    sources: { current: true, existing: true },
    phone: { current: true, existing: true },
    email: { current: true, existing: true },
    cvs: { current: true, existing: true },
  });

  const toggleExpand = (key: string) => () => {
    setExpandedRows(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleCheckboxChange = (field: string, isCurrent: boolean, { checked }: { checked: boolean }) => {
    setIsEdited(true);
    const value = (isCurrent ? currentCandidate[field] : existingCandidate[field]) as unknown[];
    const secondCheckboxValue = (isCurrent ? existingCandidate[field] : currentCandidate[field]) as unknown[];
    const secondCheckboxState =
      (document.getElementById(isCurrent ? `existing_${field}` : `current_${field}`) as HTMLInputElement | null)
        ?.checked ?? false;
    const selectedValue: unknown[] = [];
    setActiveCheckboxes(c => ({
      ...c,
      [field]: { current: isCurrent ? checked : c[field].current, existing: isCurrent ? c[field].existing : checked },
    }));
    if (checked) {
      value.forEach(val => {
        selectedValue.push(val);
      });
    }
    if (secondCheckboxState) {
      secondCheckboxValue.forEach(val => {
        if (field === 'cvs') {
          const cvVal = val as CvItem;
          if (!selectedValue.find(v => (v as CvItem).filename === cvVal.filename)) selectedValue.push(val);
        } else if (field === 'sources') {
          const srcVal = val as SourceItem;
          if (
            !selectedValue.find(
              v =>
                (v as SourceItem).source + (v as SourceItem).additional_source_name ===
                srcVal.source + srcVal.additional_source_name
            )
          )
            selectedValue.push({ ...srcVal, is_main: checked ? false : srcVal.is_main });
        } else if (!selectedValue.includes(val)) {
          selectedValue.push(val);
        }
      });
    }
    setSelectedValues(prev => ({ ...prev, [field]: selectedValue }));
  };

  const handleChange = (key: string, value: unknown, isCurrent?: boolean) => {
    setIsEdited(true);
    if (key === 'country') {
      const country = isCurrent ? currentCandidate.country : existingCandidate.country;
      const state = isCurrent ? currentCandidate.state : existingCandidate.state;
      const city = isCurrent ? currentCandidate.city : existingCandidate.city;
      setSelectedValues(prev => ({
        ...prev,
        country,
        state,
        city,
        location: `${city}${state}${country}`,
      }));
    } else {
      setSelectedValues(prev => ({ ...prev, [key]: value }));
    }
  };

  const handleEditClick = (key: string) => {
    setIsEdited(true);
    if (INLINE_EDIT_FIELDS.includes(key)) {
      setEditing(prev => ({ ...prev, [key]: true }));
    } else {
      setModalField(key);
    }
  };

  const checkAndActivateSelection = (key: string, value: unknown) => {
    if (value === currentCandidate[key]) {
      setSelectedValues(prev => ({ ...prev, [key]: currentCandidate[key] }));
    } else if (value === existingCandidate[key]) {
      setSelectedValues(prev => ({ ...prev, [key]: existingCandidate[key] }));
    }
  };

  const handleInputBlur = (key?: string, value?: unknown) => {
    if (key) {
      setSelectedValues(prev => {
        const newValues = { ...prev, [key]: value };
        checkAndActivateSelection(key, newValues[key]);
        return newValues;
      });
      setEditing(prev => ({ ...prev, [key]: false }));
    } else {
      setEditing(prev => Object.fromEntries(Object.keys(prev).map(k => [k, false])));
    }
  };

  const data = useMemo(
    () =>
      fields.map(({ field, ...fieldInfo }) => ({
        ...fieldInfo,
        field,
        currentCandidate: currentCandidate[field],
        currentCandidateData: currentCandidate,
        existingCandidate: existingCandidate[field],
        existingCandidateData: existingCandidate,
        selected: selectedValues[field],
        selectedData: selectedValues,
      })),
    [currentCandidate, existingCandidate, selectedValues]
  );

  const columns = useColumns(
    selectedValues,
    handleChange,
    activeCheckboxes,
    handleCheckboxChange,
    editing,
    handleEditClick,
    handleInputBlur,
    expandedRows,
    toggleExpand
  );

  const onMergeValidate = () => {
    if (
      selectedValues.first_name_latin &&
      selectedValues.last_name_latin &&
      selectedValues.country &&
      selectedValues.sources.length &&
      selectedValues.specialities.length
    ) {
      setError(false);
      setMergeCheckPopup(true);
    } else {
      setError(true);
    }
  };

  const onUpdateValues = (popupData: Record<string, unknown>) => {
    if (CHECKBOX_FIELDS.includes(modalField)) {
      setActiveCheckboxes(c => ({
        ...c,
        [modalField]: {
          current: isFieldSelected(currentCandidate[modalField] as unknown[], popupData, modalField),
          existing: isFieldSelected(existingCandidate[modalField] as unknown[], popupData, modalField),
        },
      }));
    }
    setSelectedValues(d => ({ ...d, ...popupData }));
    setModalField('');
  };

  const onMerge = async () => {
    setMergeCheckPopup(false);
    const uploadedCvs = await uploadDocs(
      selectedValues.cvs.filter(cv => !cv.uuid) as unknown as Parameters<typeof uploadDocs>[0]
    );
    if (uploadedCvs === null) return;
    const body: Record<string, unknown> = { ...selectedValues };
    body.cvs = [...selectedValues.cvs.filter(cv => cv.uuid).map(cv => cv.uuid), ...uploadedCvs];
    const telegramValue = selectedValues.telegram;
    let telegramRaw = '';
    if (typeof telegramValue === 'string') telegramRaw = telegramValue;
    else if (Array.isArray(telegramValue)) {
      [telegramRaw] = telegramValue;
    }
    const telegramStr = String(telegramRaw ?? '').trim();
    body.telegram = telegramStr ? [telegramStr] : [];
    body.offices = selectedValues.offices.map(o => o.uuid);
    body.state = selectedValues.state === 'Default state' ? '' : selectedValues.state || '';
    body.city = selectedValues.city || '';
    actions.setMergeProfilesAPI(id, body).then(() => {
      actions.deleteCandidate(duplicate_id, true).then(() => {
        setMergeCheckPopup(false);
        history.push(`/candidates/${id}/show`);
        notification({ message: 'Profiles successfully merged', type: NOTIFICATION_TYPES.SUCCESS });
      });
    });
  };

  const onCancelMerge = () => {
    if (isEdited) {
      setDiscardPopup(true);
    } else {
      history.push(`/dashboard/${user.active_role.name.toLowerCase().replace(/\s/g, '_')}?tab=ssc`);
    }
  };

  useEffect(() => {
    dispatch(actions.getCandidateInfo(duplicate_id, true, false, false))
      .then(sscCandidate => {
        const sscData = sscCandidate as CandidateData;
        setCurrentCandidate({
          ...sscData,
          telegram: sscData.telegram[0] || '',
          cvs: sscData.cvs.map(c => ({ filename: String(c) })),
          location: `${sscData.city}${sscData.state}${sscData.country}`,
        } as unknown as CandidateMergeData);
        setSelectedValues({
          ...sscData,
          telegram: sscData.telegram[0] || '',
          cvs: sscData.cvs.map(c => ({ filename: String(c) })),
          location: `${sscData.city}${sscData.state}${sscData.country}`,
        } as unknown as CandidateMergeData);
        if (sscData.cvs.length) {
          actions.getDocumentsByUUID(sscData.cvs as string[]).then(cvs => {
            setCurrentCandidate(c => ({ ...c, cvs: cvs as unknown as CvItem[] }));
            setSelectedValues(c => ({ ...c, cvs: cvs as unknown as CvItem[] }));
          });
        }
        dispatch(actions.getCandidateInfo(id, false, false, false))
          .then(existing => {
            const existingData = existing as CandidateData;
            setExistingCandidate({
              ...existingData,
              telegram: existingData.telegram[0] || '',
              cvs: existingData.cvs.map(c => ({ filename: String(c) })),
              location: `${existingData.city}${existingData.state}${existingData.country}`,
            } as unknown as CandidateMergeData);
            setSelectedValues(
              c =>
                ({
                  ...Object.keys(c).reduce((acc: Record<string, unknown>, key) => {
                    if (
                      c[key] !== undefined &&
                      c[key] !== null &&
                      (typeof c[key] === 'boolean' || !!(c[key] as unknown[])?.length)
                    ) {
                      acc[key] = c[key];
                    } else if (key === 'telegram') {
                      acc[key] =
                        (Array.isArray(existingData.telegram) ? existingData.telegram[0] : existingData.telegram) || '';
                    } else {
                      acc[key] = existingData[key as keyof CandidateData];
                    }
                    return acc;
                  }, {}),
                  sources: [
                    ...Object.values(
                      [
                        ...c.sources,
                        ...(existingData.sources as SourceItem[]).map((s: SourceItem) => ({ ...s, is_main: false })),
                      ].reduce((acc: Record<string, SourceItem>, obj: SourceItem) => {
                        const sourceKey = obj.source + obj.additional_source_name;
                        if (!acc[sourceKey]) acc[sourceKey] = obj;
                        return acc;
                      }, {})
                    ),
                  ],
                  phone: Array.from(new Set([...c.phone, ...existingData.phone])),
                  email: Array.from(new Set([...c.email, ...existingData.email])),
                } as CandidateMergeData)
            );
            actions.getDocumentsByUUID(existingData.cvs as string[]).then(cvs => {
              if (existingData.cvs.length) {
                setExistingCandidate(c => ({ ...c, cvs: cvs as unknown as CvItem[] }));
                setSelectedValues(c => ({
                  ...c,
                  cvs: Array.from(
                    new Map([...c.cvs, ...(cvs as unknown as CvItem[])].map(obj => [obj.filename, obj])).values()
                  ),
                }));
              }
            });
          })
          .catch(() => {
            setExistingCandidate(INITIAL_VALUES);
            setSelectedValues(INITIAL_VALUES);
          })
          .finally(() => {
            setLoadingCandidate(false);
          });
      })
      .catch(() => {
        setCurrentCandidate(INITIAL_VALUES);
      })
      .finally(() => {
        setLoadingSSCCandidate(false);
      });

    dispatch(actions.getForeignLanguages());
    dispatch(actions.getCountries());
    dispatch(actions.getCompanies({}));
    dispatch(actions.getListLocations());
    dispatch(actions.getPositions());
    dispatch(actions.getTags());
    dispatch(actions.getSpecialties());
    dispatch(actions.getCandidateSourcesAPI());
  }, []);

  return (
    <div className="wrapper-content candidates-merge-page">
      <FormCreateEdit
        title={t('translation:merge_profile.title_merge_profiles')}
        options={[]}
        onOk={onMergeValidate}
        okText={t('translation:merge_profile.button_merge_profiles')}
        onCancel={onCancelMerge}
        cancelText={t('translation:merge_profile.button_cancel')}
      >
        <Table
          error={error ? t('translation:merge_profile.error_required_fields') : ''}
          cardTitle={t('translation:merge_profile.title_data_comparison')}
          rowKey="field"
          columns={columns as unknown as TableColumnType[]}
          data={data as unknown as TableRow[]}
          loading={loadingCandidate || loadingSSCCandidate}
          showCardFooter={false}
        />
      </FormCreateEdit>
      <PopupAddCountry
        open={modalField === 'country'}
        onClose={() => setModalField('')}
        initialValues={{
          country: selectedValues.country
            ? { value: selectedValues.country, label: selectedValues.country }
            : undefined,
          state: { value: selectedValues.state, label: selectedValues.state },
          city: selectedValues.city ? { value: selectedValues.city, label: selectedValues.city } : undefined,
        }}
        onSave={onUpdateValues}
      />
      <PopupAddOffices
        open={modalField === 'offices'}
        onClose={() => setModalField('')}
        initialValues={{
          offices: selectedValues.offices.map(o => ({
            value: o.uuid,
            label: o.name,
          })),
        }}
        onSave={onUpdateValues}
      />
      <PopupAddSources
        open={modalField === 'sources'}
        onClose={() => setModalField('')}
        initialValues={{
          sources: selectedValues.sources,
        }}
        onSave={onUpdateValues}
      />
      <PopupAddLanguages
        open={modalField === 'foreign_languages'}
        onClose={() => setModalField('')}
        initialValues={{
          foreign_languages: selectedValues.foreign_languages.length
            ? selectedValues.foreign_languages.map(lang => ({
                language: { value: lang.language, label: lang.language },
                level: { value: lang.level, label: lang.level },
              }))
            : [{ language: undefined, level: undefined }],
        }}
        onSave={onUpdateValues}
      />
      <PopupAddPhone
        open={modalField === 'phone'}
        onClose={() => setModalField('')}
        initialValues={{
          phone: selectedValues.phone,
        }}
        onSave={onUpdateValues}
      />
      <PopupAddEmail
        open={modalField === 'email'}
        onClose={() => setModalField('')}
        initialValues={{
          email: selectedValues.email,
        }}
        onSave={onUpdateValues}
      />
      <PopupAddLinks
        open={modalField === 'social_networks'}
        onClose={() => setModalField('')}
        initialValues={{
          social_networks: selectedValues.social_networks,
        }}
        onSave={onUpdateValues}
      />
      <PopupAddSpecialty
        open={modalField === 'specialities'}
        onClose={() => setModalField('')}
        initialValues={{
          specialities: selectedValues.specialities.length
            ? selectedValues.specialities.map(s => ({
                speciality: s.speciality ? [{ value: s.speciality, label: s.speciality }] : undefined,
                level: { value: s.level, label: s.level },
              }))
            : [{ speciality: undefined, level: { value: levels[0], label: levels[0] } }],
        }}
        onSave={onUpdateValues}
      />
      <PopupAddDocuments
        open={modalField === 'cvs'}
        onClose={() => setModalField('')}
        initialValues={{
          cvs: selectedValues.cvs.map(d => ({ ...d, uid: d.uuid, name: d.filename })),
        }}
        onSave={onUpdateValues}
      />
      <PopupAddCVLinks
        open={modalField === 'cvs_links'}
        onClose={() => setModalField('')}
        initialValues={{
          cv_link_0: selectedValues.cvs_links[0],
          cv_link_1: selectedValues.cvs_links[1],
        }}
        onSave={onUpdateValues}
      />
      <PopupAddDescription
        open={modalField === 'description'}
        onClose={() => setModalField('')}
        initialValues={{
          description: selectedValues.description,
        }}
        onSave={onUpdateValues}
      />
      <PopupAddTags
        open={modalField === 'tags'}
        onClose={() => setModalField('')}
        initialValues={{
          tags: selectedValues.tags,
        }}
        onSave={onUpdateValues}
      />
      <PopupAddWorkingExperience
        open={modalField === 'experience'}
        onClose={() => setModalField('')}
        initialValues={{
          experience: selectedValues.experience.length
            ? selectedValues.experience.map(ex => ({
                company: ex.company ? [{ value: ex.company, label: ex.company }] : [],
                position: ex.position ? [{ value: ex.position, label: ex.position }] : [],
                worked_from: ex.worked_from ? moment(ex.worked_from, 'YYYY-MM-DD') : null,
                worked_till: ex.worked_till ? moment(ex.worked_till, 'YYYY-MM-DD') : null,
                is_current: ex.is_current,
                additional_info: ex.additional_info,
                additional_info_initial: ex.additional_info,
              }))
            : [
                {
                  company: undefined,
                  position: undefined,
                  worked_from: moment(),
                  worked_till: moment(),
                  is_current: false,
                  additional_info: '',
                  additional_info_initial: '',
                },
              ],
        }}
        onSave={onUpdateValues}
      />
      <Popup
        open={mergeCheckPopup}
        title={t('translation:merge_profile.title_merge_profiles')}
        cancelButtonProps={{
          children: t('translation:merge_profile.button_cancel'),
          onClick: () => setMergeCheckPopup(false),
        }}
        okButtonProps={{ children: t('translation:merge_profile.button_merge_profiles'), onClick: onMerge }}
      >
        <div>{t('translation:merge_profile.text_merge_confirm')}</div>
        <div>ㅤ</div>
        <div>{t('translation:merge_profile.text_merge_warning')}</div>
      </Popup>
      <Popup
        open={discardPopup}
        title={t('translation:merge_profile.title_discard_changes')}
        cancelButtonProps={{
          onClick: () => setDiscardPopup(false),
          children: t('translation:merge_profile.button_back_to_page'),
        }}
        okButtonProps={{
          onClick: () => history.push(`/dashboard/${user.active_role.name.toLowerCase().replace(/\s/g, '_')}?tab=ssc`),
          children: t('translation:merge_profile.button_discard'),
        }}
      >
        {t('translation:merge_profile.text_changes_not_saved')}
      </Popup>
    </div>
  );
};

const PopupAddCountry = ({ open, onClose, onSave, initialValues }: PopupProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const countries = useSelector(getCandidateCountries);
  const countriesOptions = useMemo(
    () =>
      countries.map(el => ({
        states: el.states.map(s => ({
          value: s.name,
          label: s.name,
          cities: s.cities.map(c => ({ value: c, label: c })),
        })),
        value: el.name,
        label: el.name,
      })),
    [countries]
  );

  const [openCity, setOpenCity] = useState(false);

  const onFinish = (data: Record<string, unknown>) => {
    onSave({
      country: (data.country as { value?: string })?.value,
      state: (data.state as { value?: string })?.value || 'Default state',
      city: (data.city as Array<{ value?: string }>)?.[0]?.value,
      location: `${(data.city as Array<{ value?: string }>)?.[0]?.value}${
        (data.state as { value?: string })?.value || 'Default state'
      }${(data.country as { value?: string })?.value}`,
    });
  };

  const onValueChange = ({ country, state, city }: Record<string, unknown>) => {
    if (country) {
      form.setFieldValue('state', undefined);
      form.setFieldValue('city', []);
    }
    if (state) {
      form.setFieldValue('city', []);
    }
    if (city) {
      form.setFieldValue('city', (city as unknown[]).slice(-1));
    }
  };

  const onCountryChange = (c: { states?: Array<{ value: string; label: string }> } | null) => {
    if (!c) {
      form.setFieldValue('state', { value: 'Default state', label: 'Default state' });
    } else if (c.states.length === 1 && c.states[0].value === 'Default state') {
      form.setFieldValue('state', c.states[0]);
    }
  };

  useEffect(() => {
    setTimeout(() => form.resetFields(), 10);
  }, [open]);

  return (
    <Popup
      open={open}
      title={t('translation:merge_profile.label_country_city')}
      cancelButtonProps={{
        onClick: onClose,
        children: t('translation:merge_profile.button_cancel'),
      }}
      okButtonProps={{
        children: t('translation:merge_profile.button_save'),
        form: 'form-edit-country',
        htmlType: 'submit',
      }}
    >
      <Form
        name="form-edit-country"
        form={form}
        initialValues={initialValues}
        onFinish={onFinish}
        onValuesChange={onValueChange}
      >
        <Row gutter={[16, 0]}>
          <Col span={24}>
            <Select
              labelInValue
              label="Country"
              options={countriesOptions}
              itemProps={{ name: 'country' }}
              onChange={onCountryChange}
              onClear={() => {
                form.setFieldValue('state', undefined);
                form.setFieldValue('city', []);
              }}
              allowClear
            />
          </Col>
          <Col span={24}>
            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.country !== curr.country}>
              {({ getFieldValue }) => {
                const options = countriesOptions.find(el => el.value === getFieldValue('country')?.value)?.states || [];
                if (!options.length || (options.length === 1 && options[0].label === 'Default state')) return null;
                return (
                  <Row gutter={[32, 0]}>
                    <Col span={24}>
                      <Select
                        labelInValue
                        label="State"
                        options={options}
                        itemProps={{ name: 'state' }}
                        disabled={!getFieldValue('country')}
                        allowClear
                      />
                    </Col>
                  </Row>
                );
              }}
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              noStyle
              shouldUpdate={(prev, curr) => prev.country !== curr.country || prev.state !== curr.state}
            >
              {({ getFieldValue }) => (
                <Select
                  className="single-select-tags"
                  open={openCity}
                  onDropdownVisibleChange={setOpenCity}
                  onChange={() => setOpenCity(false)}
                  labelInValue
                  label="City"
                  options={
                    countriesOptions
                      .find(el => el.value === getFieldValue('country')?.value)
                      ?.states.find(el => el.value === getFieldValue('state')?.value)?.cities || []
                  }
                  itemProps={{ name: 'city' }}
                  disabled={!getFieldValue('state')?.value}
                  maxTagCount={1}
                  mode={SELECT_TYPES.CREATABLE}
                  allowClear
                />
              )}
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Popup>
  );
};

const PopupAddOffices = ({ open, onClose, onSave, initialValues }: PopupProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const offices = useSelector(getLocations);
  const officesOptions = useMemo(() => offices.map(el => ({ value: el.uuid, label: el.name })), [offices]);

  const onFinish = (data: Record<string, unknown>) => {
    onSave({
      offices: (data.offices as Array<{ value: string; label: string }>).map(o => ({
        uuid: o.value,
        name: o.label,
      })),
    });
    onClose();
  };

  useEffect(() => {
    form.resetFields();
  }, [open]);

  return (
    <Popup
      open={open}
      title={t('translation:merge_profile.label_office_location')}
      cancelButtonProps={{
        onClick: onClose,
        children: t('translation:merge_profile.button_cancel'),
      }}
      okButtonProps={{
        children: t('translation:merge_profile.button_save'),
        form: 'form-edit-offices',
        htmlType: 'submit',
      }}
    >
      <Form name="form-edit-offices" form={form} initialValues={initialValues} onFinish={onFinish}>
        <Row gutter={[16, 0]}>
          <Col span={24}>
            <Select
              labelInValue
              label="Office location"
              options={officesOptions}
              itemProps={{ name: 'offices' }}
              mode={SELECT_TYPES.MULTIPLE}
              allowClear
            />
          </Col>
        </Row>
      </Form>
    </Popup>
  );
};

const PopupAddSources = ({ open, onClose, onSave, initialValues }: PopupProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const onFinish = (data: Record<string, unknown>) => {
    onSave(data);
    onClose();
  };

  useEffect(() => {
    form.resetFields();
  }, [open]);

  return (
    <Popup
      open={open}
      title={t('translation:merge_profile.label_sources')}
      cancelButtonProps={{
        onClick: onClose,
        children: t('translation:merge_profile.button_cancel'),
      }}
      okButtonProps={{
        children: t('translation:merge_profile.button_save'),
        form: 'form-edit-offices',
        htmlType: 'submit',
      }}
    >
      <Form name="form-edit-offices" form={form} initialValues={initialValues} onFinish={onFinish}>
        <FormListSources />
      </Form>
    </Popup>
  );
};

const PopupAddLanguages = ({ open, onClose, onSave, initialValues }: PopupProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const languages = useSelector(getLanguages);
  const languagesOptions = useMemo(() => languages.map(el => ({ value: el, label: el })), [languages]);
  const languageLevels = useSelector(getLevels);
  const languageLevelsOptions = useMemo(() => languageLevels.map(el => ({ value: el, label: el })), [languageLevels]);

  const onFinish = (data: Record<string, unknown>) => {
    onSave({
      foreign_languages: (data.foreign_languages as Array<{ language?: { value: string }; level?: { value: string } }>)
        .filter(el => el.language?.value)
        .map(el => ({ language: el.language!.value, level: el.level!.value })),
    });
    onClose();
  };

  useEffect(() => {
    form.resetFields();
  }, [open]);

  return (
    <Popup
      open={open}
      title={t('translation:merge_profile.label_foreign_language')}
      cancelButtonProps={{
        onClick: onClose,
        children: t('translation:merge_profile.button_cancel'),
      }}
      okButtonProps={{
        children: t('translation:merge_profile.button_save'),
        form: 'form-edit-languages',
        htmlType: 'submit',
      }}
    >
      <Form name="form-edit-languages" form={form} initialValues={initialValues} onFinish={onFinish}>
        <FormListDoubleSelect
          listName="foreign_languages"
          buttonText="Add language"
          firstSelectProps={{
            name: 'language',
            label: 'Foreign language',
            options: languagesOptions,
          }}
          secondSelectProps={{
            name: 'level',
            options: languageLevelsOptions,
          }}
          firstSelectValidator={validatorL('level', 'Please select language')}
          secondSelectValidator={validatorL('language', 'Please select language level')}
        />
      </Form>
    </Popup>
  );
};

const PopupAddPhone = ({ open, onClose, onSave, initialValues }: PopupProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const onFinish = (data: Record<string, unknown>) => {
    onSave({
      phone: (data.phone as unknown[]).filter(el => el),
    });
    onClose();
  };

  useEffect(() => {
    form.resetFields();
  }, [open]);

  return (
    <Popup
      open={open}
      title="Phone number"
      cancelButtonProps={{
        onClick: onClose,
        children: t('translation:merge_profile.button_cancel'),
      }}
      okButtonProps={{
        children: t('translation:merge_profile.button_save'),
        form: 'form-edit-phone',
        htmlType: 'submit',
      }}
    >
      <Form name="form-edit-phone" form={form} initialValues={initialValues} onFinish={onFinish}>
        <FormListSingle
          listName="phone"
          label="Phone number"
          placeholder="Enter phone number"
          buttonText="Add phone number"
          maxCount={3}
          maxLength={16}
          rules={[PHONE_VALIDATOR]}
        />
      </Form>
    </Popup>
  );
};

const PopupAddEmail = ({ open, onClose, onSave, initialValues }: PopupProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const onFinish = (data: Record<string, unknown>) => {
    onSave({
      email: (data.email as unknown[]).filter(el => el),
    });
    onClose();
  };

  useEffect(() => {
    form.resetFields();
  }, [open]);

  return (
    <Popup
      open={open}
      title="Email address"
      cancelButtonProps={{
        onClick: onClose,
        children: t('translation:merge_profile.button_cancel'),
      }}
      okButtonProps={{
        children: t('translation:merge_profile.button_save'),
        form: 'form-edit-email',
        htmlType: 'submit',
      }}
    >
      <Form name="form-edit-email" form={form} initialValues={initialValues} onFinish={onFinish}>
        <FormListSingle
          listName="email"
          label="Email address"
          placeholder="Enter email address"
          buttonText="Add email"
          maxCount={3}
          rules={[EMAIL_VALIDATION]}
        />
      </Form>
    </Popup>
  );
};

const PopupAddDescription = ({ open, onClose, onSave, initialValues }: PopupProps) => {
  const { t } = useTranslation();
  const [description, setDescription] = useState('');

  const onFinish = () => {
    onSave({ description });
    onClose();
  };

  useEffect(() => {
    setDescription(initialValues.description as string);
  }, [open, initialValues.description]);

  return (
    <Popup
      open={open}
      title="Description"
      cancelButtonProps={{
        onClick: onClose,
        children: t('translation:merge_profile.button_cancel'),
      }}
      okButtonProps={{
        children: t('translation:merge_profile.button_save'),
        form: 'form-edit-description',
        htmlType: 'submit',
      }}
    >
      <Form name="form-edit-description" initialValues={initialValues} onFinish={onFinish}>
        <RichArea
          label="Description"
          placeholder="Enter description here"
          initialValue={initialValues.description as string}
          onChange={setDescription}
          maxLength={10000}
        />
      </Form>
    </Popup>
  );
};

const PopupAddLinks = ({ open, onClose, onSave, initialValues }: PopupProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const onDetectLink = (value: string, key: number) => {
    const fields = form.getFieldsValue();
    const { social_networks } = fields;
    const network = detectNetwork(value);
    Object.assign(social_networks[key] || {}, { network });
    form.setFieldsValue({ social_networks });
  };

  const onFinish = (data: Record<string, unknown>) => {
    onSave({
      social_networks: (data.social_networks as Array<{ link?: string }>).filter(el => el.link),
    });
    onClose();
  };

  useEffect(() => {
    form.resetFields();
  }, [open]);

  return (
    <Popup
      open={open}
      title="Links"
      className="popup-edit-links"
      cancelButtonProps={{
        onClick: onClose,
        children: t('translation:merge_profile.button_cancel'),
      }}
      okButtonProps={{
        children: t('translation:merge_profile.button_save'),
        form: 'form-edit-links',
        htmlType: 'submit',
      }}
    >
      <Form name="form-edit-links" form={form} initialValues={initialValues} onFinish={onFinish}>
        <FormListDoubleInputSelect
          listName="social_networks"
          label="Links"
          buttonText="Add link"
          firstItemName="link"
          secondItemName="network"
          inputProps={{
            placeholder: 'Please enter link',
            maxLength: 1000,
            onChange: (e, f) => onDetectLink(e.target.value, f.name),
          }}
          selectProps={{
            options: SOCIAL_NETWORKS_OPTIONS,
          }}
          maxCount={5}
        />
      </Form>
    </Popup>
  );
};

const PopupAddSpecialty = ({ open, onClose, onSave, initialValues }: PopupProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [openSpeciality, setOpenSpeciality] = useState<string | number | false>(false);

  const specialties = useSelector(getCandidateSpecialities);
  const specialtiesOptions = useMemo(() => specialties.map(el => ({ value: el.name, label: el.name })), [specialties]);

  const onFinish = (data: Record<string, unknown>) => {
    onSave({
      specialities: (
        data.specialities as Array<{ speciality?: Array<{ value?: string | null }>; level: { value: string } }>
      ).map(s => ({
        speciality: s.speciality?.[0]?.value || null,
        level: s.level.value,
      })),
    });
    onClose();
  };

  const onValueChange = (
    { specialities: spec }: Record<string, unknown>,
    { specialities }: Record<string, unknown>
  ) => {
    if (spec) {
      form.setFieldValue(
        'specialities',
        (specialities as Array<{ level?: unknown; speciality?: unknown[] }>).map(s =>
          s
            ? { level: s.level, speciality: s.speciality!.slice(-1) }
            : { speciality: undefined, level: { value: levels[0], label: levels[0] } }
        )
      );
    }
  };

  useEffect(() => {
    form.resetFields();
  }, [open]);

  return (
    <Popup
      open={open}
      title="Specialty"
      cancelButtonProps={{
        onClick: onClose,
        children: t('translation:merge_profile.button_cancel'),
      }}
      okButtonProps={{
        children: t('translation:merge_profile.button_save'),
        form: 'form-edit-specialty',
        htmlType: 'submit',
      }}
    >
      <Form
        name="form-edit-specialty"
        form={form}
        initialValues={initialValues}
        onFinish={onFinish}
        onValuesChange={onValueChange}
      >
        <FormListDoubleSelect
          listName="specialities"
          buttonText="Add Specialty"
          firstSelectValidator={validatorL('level', 'Please select specialty', 'specialities')}
          secondSelectValidator={validatorL('speciality', 'Please select specialty', 'specialities')}
          firstSelectProps={{
            name: 'speciality',
            label: 'Specialty',
            required: true,
            options: specialtiesOptions,
            open: openSpeciality,
            onDropdownVisibleChange: (o, field) => {
              setOpenSpeciality(o ? field : '');
            },
            onChange: () => setOpenSpeciality(''),
            labelInValue: true,
            className: 'single-select-tags',
            maxTagCount: 1,
            mode: SELECT_TYPES.CREATABLE,
            itemProps: { rules: [REQUIRED_RULE, VALIDATION_MAX_100] },
          }}
          secondSelectProps={{
            name: 'level',
            label: 'Experience level',
            options: candidateLevels,
            allowClear: false,
          }}
          maxCount={10}
        />
      </Form>
    </Popup>
  );
};

const PopupAddDocuments = ({ open, onClose, onSave, initialValues }: PopupProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const onFinish = (data: Record<string, unknown>) => {
    onSave({
      cvs: (data.cvs as Array<{ uuid?: string; name?: string; lastModified?: number; [key: string]: unknown }>).map(
        cv => (cv.uuid ? cv : { ...cv, filename: cv.name, uploaded: cv.lastModified })
      ),
    });
    onClose();
  };

  useEffect(() => {
    form.resetFields();
  }, [open]);

  return (
    <Popup
      open={open}
      title="Documents"
      cancelButtonProps={{
        onClick: onClose,
        children: t('translation:merge_profile.button_cancel'),
      }}
      okButtonProps={{
        children: t('translation:merge_profile.button_save'),
        form: 'form-edit-cvs',
        htmlType: 'submit',
      }}
    >
      <Form name="form-edit-cvs" form={form} initialValues={initialValues} onFinish={onFinish}>
        <Upload
          accept="application/pdf, .jpg, .doc, .docx, application/vnd.ms-powerpoint, application/vnd.openxmlformats-officedocument.presentationml.slideshow, application/vnd.openxmlformats-officedocument.presentationml.presentation"
          label="Documents"
          multiple
          maxCount={5}
          itemProps={{ name: 'cvs' }}
        />
      </Form>
    </Popup>
  );
};

const PopupAddCVLinks = ({ open, onClose, onSave, initialValues }: PopupProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const onFinish = (data: Record<string, unknown>) => {
    onSave({
      cvs_links: [data.cv_link_0 as string, data.cv_link_1 as string].filter(link => !!link),
    });
    onClose();
  };

  useEffect(() => {
    form.resetFields();
  }, [open]);

  return (
    <Popup
      open={open}
      title="CV Links"
      cancelButtonProps={{
        onClick: onClose,
        children: t('translation:merge_profile.button_cancel'),
      }}
      okButtonProps={{
        children: t('translation:merge_profile.button_save'),
        form: 'form-edit-cv-links',
        htmlType: 'submit',
      }}
    >
      <Form name="form-edit-cv-links" form={form} initialValues={initialValues} onFinish={onFinish}>
        <Input label="CV links" placeholder="Enter link" itemProps={{ name: 'cv_link_0' }} />
        <Input placeholder="Enter link" itemProps={{ name: 'cv_link_1' }} />
      </Form>
    </Popup>
  );
};

const PopupAddTags = ({ open, onClose, onSave, initialValues }: PopupProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const tags = useSelector(getCandidateTags);
  const tagsOptions = useMemo(() => tags.map(el => ({ value: el.uuid, label: el.name })), [tags]);

  const onFinish = (data: Record<string, unknown>) => {
    onSave({ tags: (data.tags as Array<{ label?: string; value?: string }>).map(el => el.label || el.value) });
    onClose();
  };

  useEffect(() => {
    form.resetFields();
  }, [open]);

  return (
    <Popup
      open={open}
      title="Tags"
      cancelButtonProps={{
        onClick: onClose,
        children: t('translation:merge_profile.button_cancel'),
      }}
      okButtonProps={{
        children: t('translation:merge_profile.button_save'),
        form: 'form-edit-tags',
        htmlType: 'submit',
      }}
    >
      <Form name="form-edit-tags" form={form} initialValues={initialValues} onFinish={onFinish}>
        <Select
          labelInValue
          label="Tags"
          mode={SELECT_TYPES.CREATABLE}
          options={tagsOptions}
          itemProps={{ name: 'tags', rules: [VALIDATION_MAX_100] }}
        />
      </Form>
    </Popup>
  );
};

const ExperienceListExtra = ExperienceList as React.FC<
  React.ComponentProps<typeof ExperienceList> & { onChangeExperienceCurrent?: (key: number) => void }
>;

const PopupAddWorkingExperience = ({ open, onClose, onSave, initialValues }: PopupProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const positions = useSelector(getPositions);
  const companies = useSelector(getCandidateCompanies);

  const positionsOptions = useMemo(() => positions.map(el => ({ value: el, label: el })), [positions]);
  const companiesOptions = useMemo(() => companies.map(el => ({ value: el.uuid, label: el.name })), [companies]);

  const onChangeExperienceCurrent = (key: number) => {
    const fields = form.getFieldsValue();
    const { experience } = fields;
    Object.assign(experience[key] || {}, { worked_till: moment() });
    form.setFieldsValue({ experience });
  };

  const onValueChange = ({ experience: exp }: Record<string, unknown>, { experience }: Record<string, unknown>) => {
    if (exp) {
      form.setFieldValue(
        'experience',
        (experience as Array<Record<string, unknown>>).map((el, idx) => {
          const expArr = exp as Array<
            Record<
              string,
              | { isAfter?: (d: moment.Moment) => boolean; isBefore?: (d: moment.Moment) => boolean }
              | moment.Moment
              | undefined
            >
          >;
          const isFrom = !!expArr[idx]?.worked_from;
          const isTill = !!expArr[idx]?.worked_till;
          const worked_from = expArr[idx]?.worked_from || (el?.worked_from as moment.Moment) || moment();
          const worked_till = expArr[idx]?.worked_till || (el?.worked_till as moment.Moment) || moment();
          const workedFromMoment = moment.isMoment(worked_from) ? worked_from : moment();
          const workedTillMoment = moment.isMoment(worked_till) ? worked_till : moment();
          return {
            ...el,
            worked_from: isTill && workedFromMoment.isAfter(workedTillMoment) ? workedTillMoment : workedFromMoment,
            worked_till: isFrom && workedTillMoment.isBefore(workedFromMoment) ? workedFromMoment : workedTillMoment,
            company: (el?.company as Array<{ label?: string; value?: string }>)
              ?.slice(-1)
              .map(c => ({ ...c, label: c.label || c.value })),
            position: (el?.position as unknown[])?.slice(-1),
          };
        })
      );
    }
  };

  const onFinish = (data: Record<string, unknown>) => {
    onSave({
      experience: (
        data.experience as Array<{
          company?: Array<{ label: string }>;
          position?: Array<{ value: string }>;
          worked_from?: moment.Moment;
          worked_till?: moment.Moment;
          is_current?: boolean;
          additional_info?: string;
        }>
      )
        .filter(ex => ex.company?.length && ex.position?.length)
        .map(ex => ({
          company: ex.company![0].label,
          position: ex.position![0].value,
          worked_from: ex.worked_from ? ex.worked_from.format('YYYY-MM-DD') : null,
          worked_till: ex.worked_till ? ex.worked_till.format('YYYY-MM-DD') : null,
          is_current: ex.is_current,
          additional_info: ex.additional_info,
        })),
    });
    onClose();
  };

  useEffect(() => {
    form.resetFields();
  }, [open]);

  return (
    <Popup
      open={open}
      title="Working experience"
      className="popup-edit-working-experiences"
      cancelButtonProps={{
        onClick: onClose,
        children: t('translation:merge_profile.button_cancel'),
      }}
      okButtonProps={{
        children: t('translation:merge_profile.button_save'),
        form: 'form-edit-working-experience',
        htmlType: 'submit',
      }}
    >
      <Form
        name="form-edit-working-experience"
        form={form}
        initialValues={initialValues}
        onFinish={onFinish}
        onValuesChange={onValueChange}
      >
        <ExperienceListExtra
          onChangeExperienceCurrent={onChangeExperienceCurrent}
          companiesOptions={companiesOptions}
          positionsOptions={positionsOptions}
          maxCount={10}
          form={form}
        />
      </Form>
    </Popup>
  );
};
